import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, Copy, Loader2, History, UploadCloud } from "lucide-react";
import { toast } from "sonner";

const BADGE = {
  valid: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Ready" },
  error: { cls: "bg-rose-100 text-rose-700 border-rose-200", label: "Error" },
  duplicate: { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "Duplicate" },
};

function downloadCSV(rows, name) {
  if (!rows.length) { toast.info("Nothing to download"); return; }
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

export default function LeadImport() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const [history, setHistory] = useState([]);
  const inputRef = useRef();

  const loadHistory = useCallback(() => { api.get("/import/history").then(({ data }) => setHistory(data)).catch(() => {}); }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const getTemplate = async (fmt) => {
    try {
      const { data } = await api.get("/import/template", { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `omi-lead-import-template.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(null); setResult(null);
    runPreview(f);
  };

  const runPreview = async (f) => {
    setBusy("preview");
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/import/leads/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (ex) { toast.error(ex.response?.data?.detail || "Could not read file"); setFile(null); }
    finally { setBusy(""); }
  };

  const commit = async () => {
    if (!file) return;
    setBusy("commit");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/import/leads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.summary.imported} lead(s)`);
      loadHistory();
    } catch (ex) { toast.error(ex.response?.data?.detail || "Import failed"); }
    finally { setBusy(""); }
  };

  const reset = () => { setFile(null); setPreview(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; };

  const errorRows = preview ? preview.rows.filter((r) => r.status !== "valid").map((r) => ({
    excel_row: r.excel_row, lead_type: r.lead_type || "", name: r.name || "", mobile: r.mobile || "",
    status: r.status, reason: r.errors.map((e) => `${e.field}: ${e.reason}`).join("; ") || r.detail,
  })) : [];

  const s = preview?.summary;

  return (
    <div>
      <PageHeader title="Unified Lead Import" subtitle="One template for Insurance + Loan leads — mixed files supported" icon={UploadCloud}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => getTemplate("xlsx")} data-testid="template-xlsx-btn"><Download className="h-4 w-4 mr-1.5" />Excel</Button>
            <Button variant="outline" size="sm" onClick={() => getTemplate("csv")} data-testid="template-csv-btn"><Download className="h-4 w-4 mr-1.5" />CSV</Button>
          </div>
        } />

      <Card className="p-6 mb-6" data-testid="import-dropzone">
        <div className="flex flex-col items-center justify-center text-center gap-3 py-6 border-2 border-dashed border-border rounded-xl">
          <FileSpreadsheet className="h-10 w-10 text-primary" />
          <div>
            <div className="font-semibold">{file ? file.name : "Upload your filled template"}</div>
            <div className="text-xs text-muted-foreground mt-1">.xlsx or .csv · use the mandatory <b>lead_type</b> column (Insurance / Loan)</div>
          </div>
          <input ref={inputRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={onPick} data-testid="import-file-input" />
          <div className="flex gap-2">
            <Button onClick={() => inputRef.current?.click()} disabled={busy === "preview"} data-testid="choose-file-btn">
              {busy === "preview" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}{file ? "Choose another" : "Choose file"}
            </Button>
            {file && <Button variant="ghost" onClick={reset} data-testid="reset-import-btn">Clear</Button>}
          </div>
        </div>
      </Card>

      {s && (
        <Card className="p-5 mb-6" data-testid="import-preview">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex gap-2 flex-wrap">
              <Badge variant="outline">Total {s.total}</Badge>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Ready {s.valid}</Badge>
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Duplicate {s.duplicate}</Badge>
              <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">Error {s.error}</Badge>
            </div>
            <div className="flex gap-2">
              {errorRows.length > 0 && <Button variant="outline" size="sm" onClick={() => downloadCSV(errorRows, "omi-import-errors.csv")} data-testid="download-errors-btn"><AlertTriangle className="h-4 w-4 mr-1.5" />Error report</Button>}
              <Button size="sm" onClick={commit} disabled={busy === "commit" || s.valid === 0} data-testid="confirm-import-btn">
                {busy === "commit" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}Import {s.valid} ready
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background"><tr className="border-b border-border text-xs text-muted-foreground text-left">
                <th className="py-2 pr-3 font-medium">Row</th><th className="py-2 pr-3 font-medium">Type</th><th className="py-2 pr-3 font-medium">Name</th><th className="py-2 pr-3 font-medium">Mobile</th><th className="py-2 pr-3 font-medium">Status</th><th className="py-2 font-medium">Detail</th>
              </tr></thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.excel_row} className="border-b border-border/50" data-testid="import-preview-row">
                    <td className="py-2 pr-3 tabular text-muted-foreground">{r.excel_row}</td>
                    <td className="py-2 pr-3">{r.lead_type || "—"}</td>
                    <td className="py-2 pr-3 font-medium">{r.name || "—"}</td>
                    <td className="py-2 pr-3 tabular">{r.mobile || "—"}</td>
                    <td className="py-2 pr-3"><Badge variant="outline" className={BADGE[r.status]?.cls}>{BADGE[r.status]?.label}</Badge></td>
                    <td className="py-2 text-xs text-muted-foreground">{r.errors.length ? r.errors.map((e) => `${e.field}: ${e.reason}`).join("; ") : r.detail || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {result && (
        <Card className="p-5 mb-6 border-emerald-200" data-testid="import-result">
          <h3 className="font-bold flex items-center gap-2 mb-3"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Import Summary</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Imported", result.summary.imported, "text-emerald-600"], ["Duplicates skipped", result.summary.duplicate, "text-amber-600"], ["Errors", result.summary.error, "text-rose-600"], ["Total rows", result.summary.total, ""]].map(([l, v, c]) => (
              <div key={l} className="rounded-lg border border-border p-3"><div className="text-xs text-muted-foreground">{l}</div><div className={`text-xl font-extrabold tabular ${c}`}>{v}</div></div>
            ))}
          </div>
          {result.error_report?.length > 0 && <Button variant="outline" size="sm" className="mt-4" onClick={() => downloadCSV(result.error_report, "omi-import-errors.csv")} data-testid="result-download-errors-btn"><Download className="h-4 w-4 mr-1.5" />Download error report ({result.error_report.length})</Button>}
          <Button variant="ghost" size="sm" className="mt-4 ml-2" onClick={reset}>Import another file</Button>
        </Card>
      )}

      <Card className="p-5" data-testid="import-history-card">
        <h3 className="font-bold flex items-center gap-2 mb-4"><History className="h-4 w-4 text-primary" /> Import History</h3>
        {history.length === 0 ? <p className="text-sm text-muted-foreground">No imports yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-xs text-muted-foreground text-left">
                <th className="py-2 pr-3 font-medium">File</th><th className="py-2 pr-3 font-medium">Uploaded by</th><th className="py-2 pr-3 font-medium">Date</th><th className="py-2 pr-3 font-medium">Imported</th><th className="py-2 pr-3 font-medium">Status</th><th className="py-2 font-medium">Errors</th>
              </tr></thead>
              <tbody>
                {history.map((j) => (
                  <tr key={j.id} className="border-b border-border/50" data-testid="import-history-row">
                    <td className="py-2.5 pr-3 font-medium truncate max-w-[180px]" title={j.filename}>{j.filename}</td>
                    <td className="py-2.5 pr-3">{j.uploaded_by_name}</td>
                    <td className="py-2.5 pr-3 text-xs">{new Date(j.date).toLocaleString("en-IN")}</td>
                    <td className="py-2.5 pr-3 tabular font-semibold text-emerald-600">{j.summary?.imported ?? 0}</td>
                    <td className="py-2.5 pr-3"><Badge variant="outline" className={j.status === "completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>{j.status === "completed" ? "Completed" : "With errors"}</Badge></td>
                    <td className="py-2.5">{j.error_report?.length ? <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => downloadCSV(j.error_report, `omi-import-errors-${j.filename}.csv`)} data-testid="history-download-errors-btn"><Download className="h-3.5 w-3.5 mr-1" />{j.error_report.length}</Button> : <span className="text-muted-foreground text-xs">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
